import { useRoute, useRouter } from 'vue-router'
import { ref, computed } from 'vue'
import { defineStore, acceptHMRUpdate } from 'pinia'

import { useAccountStore } from 'src/stores/account'

import {
  filterTickets,
  getDefaultFilter,
  getDefaultSort,
  getDirectoryCounts,
  getTicketQueryDefaults,
  getTicketSortAndPaginationFromQuery,
} from 'src/services/helpers/ticket.helper'
import { DEFAULT_FILTER_SORT } from 'src/services/consts/ticket.const'
import {
  getFilterFromQuery,
  isSameTableState,
  sanitizeQuery,
  sortItems,
  paginateItems,
} from 'src/services/app/util.service'

export const useTicketsStore = defineStore('tickets', () => {
  const route = useRoute()
  const router = useRouter()
  const accountStore = useAccountStore()

  const filterBy = ref(getFilterFromQuery(getDefaultFilter(), route.query))
  const tableState = ref(getTicketSortAndPaginationFromQuery(route.query))

  // Computed
  const allTickets = computed(() => accountStore.accountData?.tickets || [])
  const totalTickets = computed(() => allTickets.value.length)
  const filteredTickets = computed(() => {
    return filterTickets(allTickets.value, filterBy.value)
  })
  const ticketsToShow = computed(() => {
    const sorted = sortItems(
      filteredTickets.value,
      tableState.value.sortBy,
      tableState.value.descending
    )
    return paginateItems(sorted, tableState.value.page, tableState.value.rowsPerPage)
  })
  const hasActiveFilters = computed(() => {
    return (
      !!filterBy.value.search || filterBy.value.dateRange !== null || tableState.value.page !== 1
    )
  })
  const directoryCounts = computed(() => {
    return getDirectoryCounts(allTickets.value)
  })

  function syncStateFromQuery(query) {
    filterBy.value = getFilterFromQuery(getDefaultFilter(), query)

    if (accountStore.isLoading || accountStore.error) return

    const totalItems = filteredTickets.value.length
    const defaultSort = getDefaultSort()
    const nextTableState = getTicketSortAndPaginationFromQuery(query, { totalItems })

    if (!isSameTableState(tableState.value, nextTableState)) {
      tableState.value = nextTableState
    }

    const urlPage = parseInt(query.page) || defaultSort.page
    if (nextTableState.page !== urlPage) {
      const combinedParams = { ...filterBy.value, ...nextTableState }
      const cleanQuery = sanitizeQuery(combinedParams, getTicketQueryDefaults(combinedParams))
      const ticketId = route.query.ticketId
      if (ticketId) {
        cleanQuery.ticketId = ticketId
      }
      router.replace({ query: cleanQuery })
    }
  }

  function resetFiltersState() {
    filterBy.value = getDefaultFilter()
    tableState.value = getDefaultSort()
  }

  function updateQuery(fieldsToUpdate) {
    // Updating the query will trigger the watch and update the state.
    if (Object.keys(fieldsToUpdate).length === 0) return
    const shouldPushToHistory =
      fieldsToUpdate.status ||
      (fieldsToUpdate.page && fieldsToUpdate.page !== tableState.value.page)
    const combinedParams = {
      ...filterBy.value,
      ...tableState.value,
      ...fieldsToUpdate,
    }

    const ticketId = route.query.ticketId
    const cleanQuery = sanitizeQuery(combinedParams, getTicketQueryDefaults(combinedParams))
    if (ticketId) {
      cleanQuery.ticketId = ticketId
    }

    const action = shouldPushToHistory ? 'push' : 'replace'
    router[action]({ query: cleanQuery })
  }
  function updateFilters(filterToUpdate) {
    updateQuery({ ...filterToUpdate, page: 1 })
  }
  function updateTableState(stateToUpdate) {
    updateQuery(stateToUpdate)
  }
  const clearFilters = () => {
    // Only clear filters and page- keeps sort and directory
    const currentStatus = filterBy.value.status
    const newFilter = { ...getDefaultFilter(), status: currentStatus }
    updateQuery({ ...newFilter, page: 1 })
  }
  const resetFilters = () => {
    // Resets everything
    updateQuery(DEFAULT_FILTER_SORT)
    filterBy.value = getDefaultFilter()
    tableState.value = getDefaultSort()
  }

  return {
    // State
    filterBy,
    tableState,
    activeDirectory: computed(() => filterBy.value.status || 'in-review'),

    // Computed
    allTickets,
    filteredTickets,
    ticketsToShow,
    totalTickets,
    hasActiveFilters,
    directoryCounts,

    // Actions
    syncStateFromQuery,
    resetFiltersState,
    updateQuery,
    updateTableState,
    updateFilters,
    clearFilters,
    resetFilters,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTicketsStore, import.meta.hot))
}
